import { ALIGNMENT_ACTION, ALIGNMENT_FILTER, LISTING_FILTER, RUN_KIND, RUN_STATUS } from './stockSyncConstants';
import { formatQuantity } from '../../utils/inventoryLabels';
import { normalizeText } from '../../utils/text';

/** `modasur.myshopify.com`, however the store's address was typed. */
export const shopDomain = (url) =>
  String(url || '')
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '')
    .trim();

/** How a store reads in a picker: its client and its address. A client has one Shopify store. */
export const channelLabel = (row) => {
  const label = [row?.channel?.company?.name, shopDomain(row?.channel?.shop_url)].filter(Boolean).join(' · ');
  return label || `Tienda #${row?.channel?.id}`;
};

/** The stores whose client keeps its stock here with the WMS: without it there is no stock to sync. */
export const usableChannels = (channels) => channels.filter((row) => row?.wms?.lite === true);

/**
 * The store a dialog opens on: the one already in use, the one of the client
 * the page is filtered by, or the only one. With several and no clue, none:
 * the person chooses, rather than syncing a store they did not mean.
 *
 * @param {Array<object>} channels - Usable ones.
 * @param {{selectedId?: number|null, companyId?: number|string|null}} [context]
 * @returns {number|null}
 */
export const pickDefaultChannelId = (channels, { selectedId = null, companyId = null } = {}) => {
  if (selectedId && channels.some((row) => row.channel.id === selectedId)) return selectedId;
  if (companyId !== null && companyId !== undefined) {
    const ofClient = channels.find((row) => String(row.channel.company?.id) === String(companyId));
    if (ofClient) return ofClient.channel.id;
  }
  return channels.length === 1 ? channels[0].channel.id : null;
};

export const isRunActive = (report) =>
  report?.status === RUN_STATUS.QUEUED || report?.status === RUN_STATUS.RUNNING;

/**
 * What stands between the store and the sync: permissions it never granted
 * (`missing`: it has to authorize Ninja again), or a store that did not say
 * (`unanswered`). `null` when nothing is known to be wrong.
 *
 * @param {object|null} detail
 * @returns {{kind: 'missing', scopes: string[]}|{kind: 'unanswered', error: string}|null}
 */
export const scopesProblemOf = (detail) => {
  if (!detail) return null;
  if (Array.isArray(detail.missing_scopes) && detail.missing_scopes.length > 0) {
    return { kind: 'missing', scopes: detail.missing_scopes };
  }
  if (detail.missing_scopes === null && detail.scopes_error) {
    return { kind: 'unanswered', error: detail.scopes_error };
  }
  return null;
};

const needsAuthorization = (detail) => scopesProblemOf(detail)?.kind === 'missing';

/**
 * The permissions comparing the stock needs besides the sync's own, or `null`.
 * A store that did not answer is told by `scopesProblemOf`.
 */
export const alignmentScopesProblemOf = (detail) =>
  Array.isArray(detail?.missing_alignment_scopes) && detail.missing_alignment_scopes.length > 0
    ? { kind: 'missing', scopes: detail.missing_alignment_scopes }
    : null;

const SCOPE_LABELS = {
  read_products: 'leer sus productos',
  read_inventory: 'leer su inventario',
  write_inventory: 'publicar stock',
  read_locations: 'leer sus ubicaciones',
  read_orders: 'leer sus órdenes',
  read_merchant_managed_fulfillment_orders: 'ver qué le falta despachar a cada orden y desde qué ubicación',
};

/**
 * The permissions the store never granted, said as what Ninja cannot do
 * without them; `forComparison` when only comparing the stock needs them.
 */
export const missingScopesText = (scopes, { forComparison = false } = {}) => {
  const labels = scopes.map((scope) => SCOPE_LABELS[scope] || scope);
  const list = labels.length > 1 ? `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}` : labels[0];
  const lead = forComparison
    ? 'Para comparar el stock, la tienda tiene que volver a autorizar a Ninja'
    : 'La tienda tiene que volver a autorizar a Ninja';
  return `${lead}: le faltan permisos para ${list}.`;
};

export const formatSyncDate = (value) =>
  value ? new Date(value).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : '';

/** «3:40 p. m.»: when a comparison stops being good, the same day it was made, as `formatSyncDate` says the hour. */
export const formatSyncTime = (value) =>
  value ? new Date(value).toLocaleTimeString('es-CL', { timeStyle: 'short' }) : '';

const RUNNING_TEXT = {
  [RUN_KIND.CATALOG]: 'Leyendo la tienda…',
  [RUN_KIND.STOCK_PREVIEW]: 'Comparando con la tienda…',
  [RUN_KIND.STOCK_ALIGNMENT]: 'Sincronizando el stock…',
};

/**
 * The line a run's card leads with: where it stands, and since when.
 *
 * @returns {{status: string, tone: string, text: string, at: string}|null}
 */
export const runHeadline = (kind, report) => {
  if (!report) return null;
  switch (report.status) {
    case RUN_STATUS.QUEUED:
      return { status: report.status, tone: 'info', text: 'En cola…', at: report.queued_at };
    case RUN_STATUS.RUNNING:
      return { status: report.status, tone: 'info', text: RUNNING_TEXT[kind], at: report.started_at };
    case RUN_STATUS.DONE:
      return { status: report.status, tone: 'success', text: 'Terminó', at: report.finished_at };
    case RUN_STATUS.FAILED:
      return { status: report.status, tone: 'danger', text: 'Falló', at: report.finished_at };
    default:
      return null;
  }
};

const counted = (count, one, many) => `${formatQuantity(count ?? 0)} ${count === 1 ? one : many}`;

/** «1 producto», «318 productos»: for the confirmations, which count what they touch. */
export const productCount = (count) => counted(count, 'producto', 'productos');

const unitCount = (count) => counted(count, 'unidad', 'unidades');

/** «Entran 7 unidades», «Sale 1 unidad»: the verb agrees with the units. */
const unitsMoving = (units, one, many) => `${units === 1 ? one : many} ${unitCount(units)}`;

/**
 * How many linked products publishing reaches: neither the paused ones, nor
 * the blocked, nor the ones never synced, which Ninja keeps out of the store
 * until their first sync. Those are switched on, so they count in ok or error.
 */
export const publishedCount = (listings) =>
  Math.max(0, (listings?.ok ?? 0) + (listings?.error ?? 0) - (listings?.not_loaded_synced ?? 0));

/** What a finished catalog sync did, a line per kind of change. */
export const catalogResultLines = (result) => {
  if (!result) return [];
  return [
    { key: 'variants', text: counted(result.variants, 'variante leída en la tienda', 'variantes leídas en la tienda') },
    { key: 'created', text: counted(result.created, 'producto nuevo en Ninja', 'productos nuevos en Ninja') },
    {
      key: 'updated',
      text: counted(result.updated, 'producto completado (EAN o peso)', 'productos completados (EAN o peso)'),
    },
    {
      key: 'links',
      text: `${counted(result.linked, 'vínculo nuevo', 'vínculos nuevos')} · ${counted(
        result.unlinked,
        'vínculo quitado',
        'vínculos quitados'
      )}`,
    },
    {
      key: 'listed',
      text: counted(result.listed, 'producto vinculado a la tienda', 'productos vinculados a la tienda'),
    },
  ];
};

/** A location as people pick it: by its name; the scannable code when it has none. */
export const slotName = (slot) => slot?.name || slot?.code || '';

/**
 * What a finished comparison found. A removal counts its whole difference
 * here, also the part that is not free: the review says how much goes out.
 */
export const previewSummaryLines = (summary) => {
  if (!summary) return [];
  const lines = [
    {
      key: 'compared',
      text: `${counted(summary.compared, 'producto comparado', 'productos comparados')} · ${counted(
        summary.aligned,
        'ya alineado',
        'ya alineados'
      )}`,
    },
  ];
  if (summary.receive?.products) {
    lines.push({
      key: 'receive',
      text: `${unitsMoving(summary.receive.units, 'Entra', 'Entran')} de ${productCount(summary.receive.products)}`,
    });
  }
  if (summary.remove?.products) {
    lines.push({
      key: 'remove',
      text: `${unitsMoving(summary.remove.units, 'Sale', 'Salen')} de ${productCount(summary.remove.products)}`,
    });
  }
  if (!summary.receive?.products && !summary.remove?.products) {
    lines.push({ key: 'nothing', text: 'No hay stock que entre ni que salga.' });
  }
  const attention = [
    summary.with_warnings ? counted(summary.with_warnings, 'con aviso', 'con aviso') : null,
    summary.skipped ? counted(summary.skipped, 'omitido', 'omitidos') : null,
  ].filter(Boolean);
  if (attention.length) lines.push({ key: 'attention', tone: 'warning', text: attention.join(' · ') });
  return lines;
};

/** What a finished alignment moved. The receipt it made is linked apart. */
export const alignmentResultLines = (result) => {
  if (!result) return [];
  const lines = [];
  if (result.received?.products) {
    const slot = slotName(result.slot);
    lines.push({
      key: 'received',
      text: `${unitsMoving(result.received.units, 'Entró', 'Entraron')} de ${productCount(result.received.products)}${
        slot ? ` en ${slot}` : ''
      }`,
    });
  }
  if (result.removed?.products) {
    lines.push({
      key: 'removed',
      text: `${unitsMoving(result.removed.units, 'Salió', 'Salieron')} de ${productCount(result.removed.products)}`,
    });
  }
  const blocks = [
    result.blocked ? counted(result.blocked, 'bloqueado', 'bloqueados') : null,
    result.unblocked ? counted(result.unblocked, 'desbloqueado', 'desbloqueados') : null,
  ].filter(Boolean);
  if (blocks.length) lines.push({ key: 'blocks', text: blocks.join(' · ') });
  if (!result.received?.products && !result.removed?.products) {
    // The rows applied without moving anything, and the products the comparison found aligned.
    const synced = (result.applied ?? 0) + (result.aligned ?? 0);
    lines.push({
      key: 'synced',
      text: `${counted(synced, 'producto quedó sincronizado', 'productos quedaron sincronizados')}, sin mover stock.`,
    });
  }
  return lines;
};

/** The variants a run left out. The report keeps five hundred at most; `total` says how many there were. */
export const skippedOf = (result) => ({
  total: result?.skipped_total ?? 0,
  rows: result?.skipped ?? [],
});

/** The removals that free stock did not cover: told, and not taken out. */
export const shortOf = (result) => result?.short ?? [];

/** Why «Sincronizar SKUs» cannot go now, or `null`. */
export const catalogSyncBlocker = (detail) => {
  if (!detail) return 'Elige la tienda';
  if (needsAuthorization(detail)) return 'La tienda tiene que volver a autorizar a Ninja';
  if (isRunActive(detail.runs?.[RUN_KIND.CATALOG])) return 'Ya hay una sincronización corriendo';
  return null;
};

/** How many linked products were never aligned with the store. */
export const notLoadedText = (listings) => {
  if (!listings?.total) return 'Todavía no hay productos vinculados.';
  const count = listings.not_loaded ?? 0;
  if (!count) return 'No queda ningún producto sin sincronizar.';
  return count === 1
    ? 'Falta 1 producto por sincronizar.'
    : `Faltan ${formatQuantity(count)} productos por sincronizar.`;
};

/** Whether this person aligns the store's stock here: who stores in the warehouse, with the whole WMS. */
export const canSyncStock = (detail) => Boolean(detail?.can_sync_stock && detail?.wms?.pro);

/** Whether this person may take stock out: an adjustment, for whoever counts inventory. */
export const canRemoveStock = (detail) => Boolean(detail?.can_remove_stock);

/** Why «Comparar con la tienda» cannot go now, or `null`. */
export const stockCompareBlocker = (detail) => {
  if (needsAuthorization(detail) || alignmentScopesProblemOf(detail)) return 'La tienda tiene que volver a autorizar a Ninja';
  if (!detail.location) return 'Elige primero la ubicación de la tienda';
  if (isRunActive(detail.runs?.[RUN_KIND.STOCK_PREVIEW])) return 'Ya hay una comparación corriendo';
  if (isRunActive(detail.runs?.[RUN_KIND.STOCK_ALIGNMENT])) return 'Ya hay una sincronización de stock corriendo';
  if (!detail.listings?.total) return 'Todavía no hay productos vinculados: sincroniza los SKUs en Productos';
  return null;
};

/** Why publishing cannot be turned on now, or `null`. Turning it off is always possible. */
export const publishBlocker = (detail) => {
  if (detail.enabled) return null;
  if (needsAuthorization(detail)) return 'La tienda tiene que volver a autorizar a Ninja';
  if (!detail.location) return 'Elige primero la ubicación de la tienda';
  return null;
};

/** What turning publishing on does, said before it goes. */
export const publishConfirmMessage = (detail) => {
  const location = detail?.location?.name;
  const count = publishedCount(detail?.listings);
  const reach = count
    ? `El stock de ${productCount(count)} en «${location}» pasa a ser el disponible de Ninja, y se actualiza con cada venta, recepción o ajuste.`
    : `Por ahora Ninja no publica ningún producto en «${location}».`;
  return [reach, publishUnloadedWarning(detail)].filter(Boolean).join(' ');
};

/**
 * The products switched on that publishing leaves out for now: never synced,
 * Ninja sends them nothing until their first sync, by hand or at night, and
 * the store keeps its own stock meanwhile.
 */
export const publishUnloadedWarning = (detail) => {
  const count = detail?.listings?.not_loaded_synced ?? 0;
  if (!count) return null;
  return count === 1
    ? '1 producto todavía no se sincronizó: Ninja no le publica stock hasta su primera sincronización, a mano o la de la noche. Mientras tanto, la tienda conserva el suyo.'
    : `${formatQuantity(count)} productos todavía no se sincronizaron: Ninja no les publica stock hasta su primera sincronización, a mano o la de la noche. Mientras tanto, la tienda conserva el suyo.`;
};

/**
 * The store's locations to choose from: the active ones, plus the one in use
 * when the store no longer lists it, so the picker still says where it is.
 */
export const locationOptions = (locations, current) => {
  const options = locations
    .filter((location) => location.active)
    .map((location) => ({ value: location.id, label: location.name }));
  if (current && !options.some((option) => option.value === current.id)) {
    options.unshift({ value: current.id, label: current.name });
  }
  return options;
};

/**
 * The filter chips over the linked products, with what the server counted.
 * «Bloqueados» shows only while some product is, or while it is the filter in use.
 */
export const listingFilterOptions = (counts, active) => [
  { value: LISTING_FILTER.OK, label: 'Sin errores', count: counts?.ok, tone: 'success' },
  { value: LISTING_FILTER.ERROR, label: 'Con error', count: counts?.error, tone: 'danger' },
  ...(counts?.blocked || active === LISTING_FILTER.BLOCKED
    ? [{ value: LISTING_FILTER.BLOCKED, label: 'Bloqueados', count: counts?.blocked, tone: 'warning' }]
    : []),
  { value: LISTING_FILTER.DISABLED, label: 'Pausados', count: counts?.disabled, tone: 'neutral' },
  { value: LISTING_FILTER.NOT_LOADED, label: 'Sin sincronizar', count: counts?.not_loaded, tone: 'warning' },
];

/** The line under «Publicar»: how the linked products stand. */
export const publishSummary = (counts) =>
  [
    counted(counts?.ok, 'sin errores', 'sin errores'),
    counted(counts?.error, 'con error', 'con error'),
    counted(counts?.disabled, 'pausado', 'pausados'),
    ...(counts?.blocked ? [counted(counts.blocked, 'bloqueado', 'bloqueados')] : []),
  ].join(' · ');

/**
 * Where one linked product stands with the store. A blocked one says so
 * first (an alignment held it: the store has orders Ninja does not), then
 * a paused one: nothing else about them moves.
 *
 * @returns {{key: string, label: string, tone: string}}
 */
export const listingStateOf = (row) => {
  if (row.blocked_reason) return { key: 'blocked', label: 'Bloqueado', tone: 'warning' };
  if (!row.sync_enabled) return { key: 'paused', label: 'Pausado', tone: 'neutral' };
  if (row.push_pending) return { key: 'pending', label: 'En cola', tone: 'info' };
  if (row.last_sync_error) return { key: 'error', label: 'Error', tone: 'danger' };
  if (row.last_synced_at) return { key: 'synced', label: 'Al día', tone: 'success' };
  return { key: 'unpublished', label: 'Sin publicar', tone: 'neutral' };
};

export const hasPendingListings = (rows) => rows.some((row) => row.sync_enabled && row.push_pending);

/**
 * Why «Publicar ahora» cannot go for one product, or `null`. The backend
 * skips the same ones, answering `queued: false`: one never synced gets
 * nothing until its first sync.
 */
export const pushBlocker = (row, detail) => {
  if (!detail?.enabled) return 'La publicación de la tienda está apagada';
  if (row.blocked_reason) return 'Bloqueado: Ninja no lo publica';
  if (!row.sync_enabled) return 'Pausado: reanúdalo para publicarlo';
  if (!row.initial_loaded_at) {
    return 'Sin sincronizar: Ninja lo publica después de su primera sincronización, a mano o la de la noche';
  }
  return null;
};

/**
 * What the toast says when a run ends, with the numbers it left.
 *
 * @returns {{type: 'success'|'error', message: string}|null}
 */
const RUN_NAME = {
  [RUN_KIND.CATALOG]: 'La sincronización de SKUs',
  [RUN_KIND.STOCK_PREVIEW]: 'La comparación con la tienda',
  [RUN_KIND.STOCK_ALIGNMENT]: 'La sincronización de stock',
};

/** «entran 7 unidades · salen 9», the units each way; nothing when none moves. */
const movesText = (inbound, outbound, [inVerb, outVerb]) =>
  [
    inbound ? `${inVerb[inbound === 1 ? 0 : 1]} ${unitCount(inbound)}` : null,
    outbound ? `${outVerb[outbound === 1 ? 0 : 1]} ${inbound ? formatQuantity(outbound) : unitCount(outbound)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

export const runEndedNotice = (kind, report) => {
  if (report?.status === RUN_STATUS.FAILED) {
    return { type: 'error', message: `${RUN_NAME[kind]} falló: ${report.error || 'sin detalle'}` };
  }
  if (report?.status !== RUN_STATUS.DONE) return null;

  const result = report.result || {};
  if (kind === RUN_KIND.STOCK_PREVIEW) {
    const { summary = {} } = result;
    const moves = movesText(summary.receive?.units, summary.remove?.units, [
      ['entra', 'entran'],
      ['sale', 'salen'],
    ]);
    const skipped = summary.skipped ? ` · ${counted(summary.skipped, 'omitido', 'omitidos')}` : '';
    const found = moves ? `${moves}${skipped}` : `el stock ya está igual al de la tienda${skipped}`;
    return {
      type: 'success',
      message: `Comparación lista: ${found}. Aplícala antes de las ${formatSyncTime(result.expires_at)}.`,
    };
  }

  const skipped = result.skipped_total ? ` · ${counted(result.skipped_total, 'omitido', 'omitidos')}` : '';
  if (kind === RUN_KIND.CATALOG) {
    return {
      type: 'success',
      message: `SKUs sincronizados: ${counted(result.created, 'producto nuevo', 'productos nuevos')} · ${counted(
        result.listed,
        'vinculado',
        'vinculados'
      )}${skipped}`,
    };
  }
  const moves = movesText(result.received?.units, result.removed?.units, [
    ['entró', 'entraron'],
    ['salió', 'salieron'],
  ]);
  return {
    type: 'success',
    message: `Stock sincronizado: ${moves || 'sin mover stock'}${skipped}`,
  };
};

/* The comparison under review. */

export const PREVIEW_STATE = {
  NONE: 'none',
  RUNNING: 'running',
  FAILED: 'failed',
  READY: 'ready',
  EXPIRED: 'expired',
  APPLIED: 'applied',
};

/** What applying a comparison sends back, and what tells two comparisons apart. */
export const previewTokenOf = (report) => report?.result?.token ?? null;

/** A comparison as the store's runs keep it: without its rows, which may weigh megabytes. */
export const previewSummaryOf = (report) => {
  if (!report?.result) return report ?? null;
  const { rows, aligned, ...result } = report.result;
  return { ...report, result };
};

/** A comparison is good for an hour; `expiredToken` is the one the clock already let go. */
export const isPreviewExpired = (report, expiredToken = null, now = Date.now()) => {
  const token = previewTokenOf(report);
  if (!token) return false;
  if (expiredToken === token) return true;
  const expiresAt = report.result?.expires_at;
  return Boolean(expiresAt) && Date.parse(expiresAt) <= now;
};

/** Where a comparison stands: none, on its way, failed, ready to apply, expired or applied. */
export const previewStateOf = (report, expiredToken = null, now = Date.now()) => {
  if (!report) return PREVIEW_STATE.NONE;
  if (isRunActive(report)) return PREVIEW_STATE.RUNNING;
  if (report.status === RUN_STATUS.FAILED) return PREVIEW_STATE.FAILED;
  if (report.applied_at) return PREVIEW_STATE.APPLIED;
  if (isPreviewExpired(report, expiredToken, now)) return PREVIEW_STATE.EXPIRED;
  return PREVIEW_STATE.READY;
};

const REMOVE_NOT_ALLOWED = 'Sacar stock es un ajuste de inventario: lo hace quien tiene permiso de conteo';

/**
 * Whether a row can be marked. Left-out ones never: a blocked one is blocked
 * on applying anyway. A removal only by whoever may adjust stock.
 */
export const isAlignmentRowSelectable = (row, canRemove) =>
  row.action !== ALIGNMENT_ACTION.SKIP && (row.action !== ALIGNMENT_ACTION.REMOVE || canRemove);

/** Why a row cannot be marked, for its switched-off box. */
export const alignmentSelectionHint = (row, canRemove) => {
  if (row.action === ALIGNMENT_ACTION.SKIP) {
    return row.block ? 'Omitido: se bloquea al aplicar, sin marcarlo' : 'Omitido: no cambia al aplicar';
  }
  if (row.action === ALIGNMENT_ACTION.REMOVE && !canRemove) return REMOVE_NOT_ALLOWED;
  return undefined;
};

/** The units a row really moves: a removal leaves in what is not free (`short`). */
const movedUnits = (row) => Math.max(0, (row.units ?? 0) - (row.short ?? 0));

const matchesAlignmentFilter = (row, filter) => {
  if (filter === ALIGNMENT_FILTER.ALL) return true;
  if (filter === ALIGNMENT_FILTER.WARNINGS) return (row.warnings?.length ?? 0) > 0;
  return row.action === filter;
};

/** The rows the chip and the search leave, both on the whole comparison. */
export const filterAlignmentRows = (rows, { search = '', action = ALIGNMENT_FILTER.ALL } = {}) => {
  const needle = normalizeText(search.trim());
  return rows.filter(
    (row) =>
      matchesAlignmentFilter(row, action) &&
      (!needle || normalizeText(row.sku).includes(needle) || normalizeText(row.title).includes(needle))
  );
};

/** How many rows each chip holds. */
export const alignmentCounts = (rows) => ({
  all: rows.length,
  receive: rows.filter((row) => row.action === ALIGNMENT_ACTION.RECEIVE).length,
  remove: rows.filter((row) => row.action === ALIGNMENT_ACTION.REMOVE).length,
  warnings: rows.filter((row) => (row.warnings?.length ?? 0) > 0).length,
  skip: rows.filter((row) => row.action === ALIGNMENT_ACTION.SKIP).length,
});

/** The chips over the rows. «Con aviso» crosses the others. */
export const alignmentFilterOptions = (counts) => [
  { value: ALIGNMENT_FILTER.RECEIVE, label: 'Entra', count: counts.receive, tone: 'success' },
  { value: ALIGNMENT_FILTER.REMOVE, label: 'Sale', count: counts.remove, tone: 'danger' },
  { value: ALIGNMENT_FILTER.WARNINGS, label: 'Con aviso', count: counts.warnings, tone: 'warning' },
  { value: ALIGNMENT_FILTER.SKIP, label: 'Omitidos', count: counts.skip, tone: 'neutral' },
];

/**
 * What applying would send and do: the marked rows that can be marked, the
 * units each way, what stays in for not being free, the rows that only get
 * synced, and the blocked ones, which go with no mark.
 *
 * @returns {{ids: number[], count: number, receive: {products: number, units: number},
 *   remove: {products: number, units: number}, short: number, none: number, blocks: number}}
 */
export const alignmentSelectionOf = (rows, selectedIds, canRemove) => {
  const marked = new Set(selectedIds);
  const selection = {
    ids: [],
    count: 0,
    receive: { products: 0, units: 0 },
    remove: { products: 0, units: 0 },
    short: 0,
    none: 0,
    blocks: 0,
  };
  rows.forEach((row) => {
    if (row.block) selection.blocks += 1;
    if (!marked.has(row.listing_id) || !isAlignmentRowSelectable(row, canRemove)) return;
    selection.ids.push(row.listing_id);
    selection.count += 1;
    if (row.action === ALIGNMENT_ACTION.RECEIVE) {
      selection.receive.products += 1;
      selection.receive.units += row.units ?? 0;
    } else if (row.action === ALIGNMENT_ACTION.REMOVE) {
      selection.remove.products += 1;
      selection.remove.units += movedUnits(row);
      selection.short += row.short ?? 0;
    } else {
      selection.none += 1;
    }
  });
  return selection;
};

/** «22 marcados · entran 340 unidades de 20 productos · salen 3 unidades de 1 producto». */
export const alignmentSelectionText = (selection) =>
  [
    counted(selection.count, 'marcado', 'marcados'),
    selection.receive.products
      ? `${unitsMoving(selection.receive.units, 'entra', 'entran')} de ${productCount(selection.receive.products)}`
      : null,
    selection.remove.products
      ? `${unitsMoving(selection.remove.units, 'sale', 'salen')} de ${productCount(selection.remove.products)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** «1 producto ya alineado queda sincronizado»: what applying does with nothing to move. */
export const alignedSyncedText = (aligned) =>
  aligned === 1
    ? '1 producto ya alineado queda sincronizado'
    : `${formatQuantity(aligned)} productos ya alineados quedan sincronizados`;

/**
 * What applying the marked rows does, said before it goes. With nothing
 * marked it still syncs the products already aligned (`aligned`).
 */
export const applyConfirmMessage = ({ selection, slot, publishing, aligned = 0 }) => {
  const moves = [];
  if (selection.receive.products) {
    moves.push(
      `${unitsMoving(selection.receive.units, 'Entra', 'Entran')} de ${productCount(selection.receive.products)} a ${slotName(
        slot
      )} como una recepción`
    );
  }
  if (selection.remove.products) {
    const verb = moves.length ? ['sale', 'salen'] : ['Sale', 'Salen'];
    moves.push(`${unitsMoving(selection.remove.units, ...verb)} de ${productCount(selection.remove.products)} con un ajuste`);
  }
  return [
    moves.length ? `${moves.join(', y ')}.` : null,
    selection.short === 1 ? 'Otra unidad no alcanza a salir: no está libre.' : null,
    selection.short > 1
      ? `Otras ${formatQuantity(selection.short)} unidades no alcanzan a salir: no están libres.`
      : null,
    selection.none
      ? `${productCount(selection.none)} ${selection.none === 1 ? 'queda sincronizado' : 'quedan sincronizados'} sin mover stock.`
      : null,
    !selection.count && aligned ? `No se mueve stock: ${alignedSyncedText(aligned)}.` : null,
    selection.blocks
      ? `${productCount(selection.blocks)} ${selection.blocks === 1 ? 'se bloquea' : 'se bloquean'}.`
      : null,
    publishing ? 'Con «Publicar» encendido, la tienda queda con lo que Ninja publica después.' : null,
  ]
    .filter(Boolean)
    .join(' ');
};

/** «Entra 5», «Sale 1», «Nada», «Omitido»: what applying does to the row. */
export const alignmentActionText = (row) => {
  if (row.action === ALIGNMENT_ACTION.RECEIVE) return `${row.action_label} ${formatQuantity(row.units)}`;
  if (row.action === ALIGNMENT_ACTION.REMOVE) return `${row.action_label} ${formatQuantity(movedUnits(row))}`;
  return row.action_label;
};

/** «4 → 12»: what Ninja publishes now, and after applying. */
export const alignmentPublishText = (row) => {
  if (row.publishes_after === null || row.publishes_after === undefined) return '—';
  if (row.publishes_after === row.publishes) return formatQuantity(row.publishes);
  return `${formatQuantity(row.publishes)} → ${formatQuantity(row.publishes_after)}`;
};

const signed = (value) => (value > 0 ? `+${formatQuantity(value)}` : formatQuantity(value));

/** What there is to know of a row before applying it, each its own line. */
export const alignmentRowNotes = (row) => {
  const notes = [];
  if (row.block) notes.push({ key: 'block', tone: 'warning', text: 'Se bloquea al aplicar' });
  if (row.unblock) notes.push({ key: 'unblock', tone: 'info', text: 'Se desbloquea al aplicar' });
  if (row.reason_label) {
    notes.push({
      key: 'reason',
      tone: 'warning',
      text: row.orders?.length ? `${row.reason_label}: ${row.orders.join(', ')}` : row.reason_label,
    });
  }
  (row.warnings ?? []).forEach((warning) => {
    const drift =
      warning.code === 'store_drift' && row.drift
        ? ` (${signed(row.drift)}${row.drift_since ? ` desde el ${formatSyncDate(row.drift_since)}` : ''})`
        : '';
    notes.push({ key: `warning-${warning.code}`, tone: 'warning', text: `${warning.label}${drift}` });
  });
  if (row.short) {
    notes.push({
      key: 'short',
      tone: 'warning',
      text: `Lo libre no alcanza: ${counted(row.short, 'unidad no sale', 'unidades no salen')}`,
    });
  }
  // The store already took them off: no warning, only why it goes out.
  if (row.store_sales) {
    notes.push({
      key: 'store-sales',
      tone: 'info',
      text: `Incluye ${counted(row.store_sales, 'unidad vendida', 'unidades vendidas')} en la tienda con un envío que Ninja no despacha`,
    });
  }
  return notes;
};

/** What a nightly sync left for someone to review: it applies only what the review would mark. */
export const reviewOf = (result) => ({
  total: result?.review_total ?? 0,
  rows: result?.review ?? [],
});

/** «3 productos quedaron para revisar…», with its pronouns in the singular for one. */
export const reviewLabel = (total) =>
  total === 1
    ? '1 producto quedó para revisar: compáralo y aplícalo a mano'
    : `${formatQuantity(total)} productos quedaron para revisar: compáralos y aplícalos a mano`;

/** Why a product was left for review: its reason, or its warnings. */
export const reviewRowNote = (row) =>
  row.reason_label || (row.warnings ?? []).map((warning) => warning.label).join(' · ');

/** What lies behind Ninja's count of a row, for its tooltip. */
export const alignmentNinjaDetail = (row) =>
  [
    row.explained ? `${formatQuantity(row.explained)} que Ninja todavía no vende (muelle, cuarentena, inactivo o con reporte)` : null,
    row.other_origins ? `${formatQuantity(row.other_origins)} guardadas para órdenes que la tienda no conoce` : null,
    row.unsellable ? `${formatQuantity(row.unsellable)} no vendibles` : null,
    row.sent !== null && row.sent !== undefined ? `Lo último que Ninja envió: ${formatQuantity(row.sent)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Why «Aplicar» cannot go now, or `null`. With nothing marked it still goes
 * while it syncs something: the aligned products, or the ones it blocks.
 */
export const applyBlocker = ({ detail, previewState, selection, aligned = 0 }) => {
  if (needsAuthorization(detail) || alignmentScopesProblemOf(detail)) return 'La tienda tiene que volver a autorizar a Ninja';
  if (previewState === PREVIEW_STATE.APPLIED) return 'Esta comparación ya se aplicó';
  if (previewState === PREVIEW_STATE.EXPIRED) return 'La comparación venció: compara de nuevo';
  if (previewState !== PREVIEW_STATE.READY) return 'Todavía no hay una comparación lista';
  if (isRunActive(detail?.runs?.[RUN_KIND.STOCK_ALIGNMENT])) return 'Ya hay una sincronización de stock corriendo';
  if (!selection.count && !selection.blocks && !aligned) return 'Marca al menos un producto';
  if (selection.receive.products && !detail?.initial_load_slot) return 'Elige la ubicación de entrada';
  return null;
};
